import {
  DigimapViewer,
  TDGMMapViewerOptions,
} from '@digimap/digimap-core-viewer';
import ChatBotWidget from 'chatbot/ChatBotWidget';
import { AppConfig } from 'config';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageHandler } from 'utils/MessageHandler';

type HostConfig = {
  mode: NonNullable<TDGMMapViewerOptions['mode']>;
  id: string;
  apiKey: string;
  lang: string;
  exportTable: boolean;
  showGPS: boolean;
  showExportPDF?: boolean;
};

const HOST_CONFIGS: Record<string, HostConfig> = {
  'hcj.digimap.ai': {
    mode: 'v2',
    id: 'cc00460b-44d0-4c41-b3ab-1c8c11cad131',
    apiKey: 'wPJ4cU-bdce0g5ESnBqp7kCO4t3MSFbQbbt2pHCGH80',
    lang: 'ja',
    exportTable: true,
    showGPS: false,
    showExportPDF: true,
  },
  'foodex.digimap.ai': {
    mode: 'v2',
    id: '6ff5a059-0383-4ada-9633-4d67cb6a4af4',
    apiKey: 'VJz7vX8HzthXr66VO27O2UElEFUanOUdneXCZ8gXj8w',
    lang: 'ja',
    exportTable: true,
    showGPS: false,
    showExportPDF: true,
  },
  'vec.digimap.ai': {
    mode: 'v3',
    id: '93eb1ca4-dfe5-4d3c-8f0c-b445a3fc1f19',
    apiKey: 'uNv1sFs-dp-mvWeKJZRZo5kntvdovRel0miBB5WTjkE',
    lang: 'vi',
    exportTable: false,
    showGPS: true,
  },
  'vec.digimap.ai/autumn': {
    mode: 'v2',
    id: '1f0d9d4c-21b2-43d4-94b7-4222e43a66cc',
    apiKey: 'LaiSLfzCPKSbuLENMjvKhwxrst_0VLffj5fqkXnUDWU',
    lang: 'vi',
    exportTable: false,
    showGPS: true,
  },
  'vec.digimap.ai/a80': {
    mode: 'v2',
    id: '86235967-7851-41c4-8baa-00c75adb7871',
    apiKey: 'WIkgJchGMWikWKAbSACjMOAx0qs1UhQIsX9ChOlA-XQ',
    lang: 'vi',
    exportTable: false,
    showGPS: true,
  },
  'techfestvietnam2025.digimap.ai': {
    mode: 'v2',
    id: '2ed755c9-56e6-4f74-8d26-62e9b727ccb6',
    apiKey: 'cfOXkxkk0ZY4n-_m3OYZ4akk8KXYWJijd30l3P3Uff8',
    lang: 'vi',
    exportTable: false,
    showGPS: true,
  },
};

const VEC_IDS = [
  '1f0d9d4c-21b2-43d4-94b7-4222e43a66cc',
  '4944b150-4c7a-4077-a489-7edf1e939dd2',
];

const parseNumber = (
  searchParams: URLSearchParams,
  key: string,
): number | undefined => {
  const value = Number(searchParams.get(key));
  return Number.isNaN(value) ? undefined : value;
};

const parseBoolean = (
  searchParams: URLSearchParams,
  key: string,
  defaultValue: boolean,
): boolean => {
  const raw = searchParams.get(key);
  if (raw === null) return defaultValue;
  return raw === 'true';
};

export function App() {
  const [searchParams] = useSearchParams();
  const [showChatBot, setShowChatBot] = useState(false);

  useEffect(() => {
    let mode =
      (searchParams.get('mode') as TDGMMapViewerOptions['mode']) ?? 'v3';
    let id = searchParams.get('venue') ?? '';
    let apiKey = searchParams.get('key') ?? '';
    let defaultLanguage = searchParams.get('lang') ?? undefined;
    const exportPDF = parseBoolean(searchParams, 'exportPDF', false);

    const offsetTop = parseNumber(searchParams, 'offsetTop');
    const offsetBottom = parseNumber(searchParams, 'offsetBottom');
    const offsetTopButton = parseNumber(searchParams, 'topButton');

    let showGPS = parseBoolean(searchParams, 'showGPS', true);
    let exportTable = parseBoolean(searchParams, 'exportTable', false);
    const showLanguageSwitcher = parseBoolean(
      searchParams,
      'showLanguageSwitcher',
      true,
    );
    let showExportPDF = parseBoolean(searchParams, 'showExportPDF', false);
    const platform = searchParams.get('platform') ?? undefined;
    let isMobileMode = parseBoolean(searchParams, 'isMobileMode', false);

    const host = window.location.hostname;
    const pathname = window.location.pathname;
    const fullHost = pathname !== '/' ? `${host}${pathname}` : host;

    if (HOST_CONFIGS[fullHost]) {
      const hostConfig = HOST_CONFIGS[fullHost];
      mode = hostConfig.mode;
      id = hostConfig.id;
      apiKey = hostConfig.apiKey;
      if (!defaultLanguage) {
        defaultLanguage = hostConfig.lang;
      }
      exportTable = hostConfig.exportTable;
      showGPS = hostConfig.showGPS;
      showExportPDF = hostConfig.showExportPDF ?? false;
    }

    if (!defaultLanguage) {
      defaultLanguage = 'en';
    }

    if (id === 'df22acd2-4ed8-45b6-9c33-c4f3b36bed86') {
      showGPS = false;
      exportTable = false;
      defaultLanguage = 'ja';
    }

    setShowChatBot(VEC_IDS.includes(id));

    const options: TDGMMapViewerOptions = {
      id,
      apiKey,
      draftMode: (searchParams.get('type') || 'public') === 'draft',
      debugMode: searchParams.get('debug') === 'true',
      defaultLanguage,
      dev:
        window.location.hostname.includes('dev') ||
        window.location.hostname.includes('localhost'),
      mode,
      exportPDF,
      // openSearch: {
      //   endpoint: AppConfig.opensearchUrl,
      //   username: AppConfig.usernameOpenSearch,
      //   password: AppConfig.passwordOpenSearch,
      // },
      offset: {
        top: offsetTop,
        bottom: offsetBottom,
        topButton: offsetTopButton,
      },
      // eventLogs: {
      //   endpoint: AppConfig.eventLogsUrl,
      //   apiKey: AppConfig.eventLogsApiKey,
      // },
      config: {
        exportTable,
        showGPS,
        platform: platform as 'iOS' | 'android' | undefined,
        showLanguageSwitcher,
        showExportPDF,
        isMobileMode
      },
    };
    DigimapViewer.init(options);
  }, [searchParams]);

  useEffect(() => {
    const messageHandler = new MessageHandler();
    messageHandler.init();

    return () => {
      messageHandler.destroy();
    };
  }, []);

  return (
    <div style={{ width: '100%', height: '100%' }} id="digimap-viewer">
      {showChatBot && <ChatBotWidget />}
    </div>
  );
}
